import React from 'react';
import { Field as FinalFormField } from 'react-final-form';
import { Field as ReduxFormField } from 'redux-form';
import { Alert } from '@/ui';
import { CriteriaPanel } from './CriteriaPanel';
import { SourceType, CriteriaData } from './types';

interface CriteriaFieldValue {
  criteriaConfiguration?: string;
  tags?: string | null;
  createdAt?: string;
  updatedAt?: string;
  validationSummary?: string | null;
  validationErrors?: Record<string, unknown>;
  isValid?: boolean;
}

interface FieldProps {
  input: {
    value: CriteriaFieldValue | undefined;
    onChange: (value: CriteriaData) => void;
  };
  meta: {
    error?: string;
    touched?: boolean;
    submitFailed?: boolean;
    modifiedSinceLastSubmit?: boolean;
  };
  sourceType: SourceType;
  hideCapsTab?: boolean;
}

interface UnifiedFormCriteriaPanelProps {
  name: string;
  sourceType?: SourceType;
  formType?: 'react-final-form' | 'redux-form';
  hideCapsTab?: boolean;
}

const parseCriteria = (fieldValue: CriteriaFieldValue | undefined): CriteriaData => {
  const now = new Date().toISOString();

  if (!fieldValue) {
    return {
      criteriaConfiguration: '{}',
      tags: {},
      createdAt: now,
      updatedAt: now,
    };
  }

  return {
    criteriaConfiguration: fieldValue.criteriaConfiguration || '{}',
    tags: fieldValue.tags ? { value: fieldValue.tags } : {},
    createdAt: fieldValue.createdAt || now,
    updatedAt: fieldValue.updatedAt || now,
    validationSummary: fieldValue.validationSummary ?? null,
    validationErrors: fieldValue.validationErrors,
    isValid: fieldValue.isValid,
  };
};

/**
 * The field's render target. A component rather than a render function so the parsed criteria can
 * be memoised on the field value: CriteriaPanel rebuilds and re-validates its whole state whenever
 * the `criteria` object it receives changes identity, and a fresh object on every render meant it
 * did that for every unrelated change to the form — each offer dropped into a sequence stack, say.
 */
const CriteriaField = ({ input, meta, sourceType, hideCapsTab }: FieldProps) => {
  const parsedCriteria = React.useMemo(() => parseCriteria(input.value), [input.value]);

  const handleCriteriaChange = (criteriaData: CriteriaData) => {
    input.onChange(criteriaData);
  };

  // Only show errors if submit failed AND user hasn't modified the field since then
  const showMetaError = Boolean(meta.error && meta.submitFailed && !meta.modifiedSinceLastSubmit);
  const showValidationSummary = Boolean(
    parsedCriteria.validationSummary && 
    !showMetaError && 
    meta.submitFailed && 
    !meta.modifiedSinceLastSubmit
  );
  // Show all validation errors only when form submission was attempted and failed, and field hasn't been modified since
  const showAllErrors = Boolean(meta.submitFailed && !meta.modifiedSinceLastSubmit);

  // A fragment, not a div: the three things here are grouped for React, not laid out
  // together, and an extra block box between the panel and the page broke the page's
  // card rhythm — a card with a wrapper around it has no card to be spaced from.
  return (
    <>
      <CriteriaPanel
        sourceType={sourceType}
        criteria={parsedCriteria}
        onChange={handleCriteriaChange}
        showAllErrors={showAllErrors}
        hideCapsTab={hideCapsTab}
      />
      {showMetaError && <Alert color='danger'>{meta.error}</Alert>}
      {showValidationSummary && <Alert color='danger'>{parsedCriteria.validationSummary}</Alert>}
    </>
  );
};

export function UnifiedFormCriteriaPanel({
  name,
  sourceType = 'default',
  formType = 'react-final-form',
  hideCapsTab = false,
}: UnifiedFormCriteriaPanelProps) {
  if (formType === 'redux-form') {
    return (
      <ReduxFormField
        name={name}
        component={CriteriaField}
        sourceType={sourceType}
        hideCapsTab={hideCapsTab}
      />
    );
  }

  return (
    <FinalFormField<CriteriaFieldValue> name={name}>
      {(fieldProps) => <CriteriaField {...fieldProps} sourceType={sourceType} hideCapsTab={hideCapsTab} />}
    </FinalFormField>
  );
}
